import { Record, List } from 'immutable';

import Hydrator from 'utils/Hydrator';
import { formatCurrency } from 'utils/NumberHelper';

import { CourseType, CourseColor } from 'enums/course-type';
import { CourseProgressType } from 'enums/course-progress-type';
import { formatISODate, formatMinutes } from 'utils/DateHelper';

import CourseModule from './CourseModuleModel';
import CourseLeaderModel from './CourseLeaderModel';

const CourseRecord = Record({
  id: 0,
  type: '',
  title: '',
  description: '',
  descriptionLong: '',
  totalTimeInMinutes: 0,
  remainderTimeInMinutes: 0,
  price: 0,
  courseLeader: {},
  modules: List(),
  progress: 0,
  modulesCompleted: 0,
  lessonsCompleted: 0,
  lessonsCount: 0,
  locked: false,
  preview: false,
  badgeUrl: undefined,
  cpdBadgeUrl: undefined,
  completedAt: new Date().toISOString(),
});

export default class Course extends CourseRecord {
  static deserialize(jsonObject = {}) {
    if (!jsonObject) {
      return undefined;
    }

    const mapProperties = {
      modules: Hydrator.fromList(CourseModule.deserialize),
      courseLeader: CourseLeaderModel.deserialize,
    };

    const hydrated = Object.keys(jsonObject).reduce(Hydrator.assign(jsonObject, mapProperties), {});

    return new Course(hydrated);
  }

  get completePercentage() {
    return Math.round((this.lessonsCompleted / this.lessonsCount) * 100);
  }

  get modulesCount() {
    return this.modules.size;
  }

  get color() {
    return CourseColor[this.type];
  }

  getPrice() {
    if (this.price === 0) {
      return 'FREE';
    }

    return formatCurrency(this.price);
  }

  getTotalTimeFormatted() {
    return formatMinutes(this.totalTimeInMinutes);
  }

  getRemainderTimeFormatted() {
    return formatMinutes(this.remainderTimeInMinutes);
  }

  getDisplayTime() {
    return this.isStarted() ? this.getRemainderTimeFormatted() : this.getTotalTimeFormatted();
  }

  isCore() {
    return this.type === CourseType.core;
  }

  isAdviser() {
    return this.type === CourseType.adviser;
  }

  isMastery() {
    return this.type === CourseType.mastery;
  }

  metaData() {
    return {
      courseId: this.id,
      courseTitle: this.title,
      coursePercentage: this.completePercentage,
      modulesCompleted: this.modulesCompleted,
      lessonsCompleted: this.lessonsCompleted,
    };
  }

  isNotStarted() {
    return this.progress === CourseProgressType.notStarted;
  }

  isStarted() {
    return this.progress === CourseProgressType.started;
  }

  isCompleted() {
    return this.progress === CourseProgressType.completed;
  }

  getFirstLesson() {
    return this.getIn(['modules', 0, 'lessons', 0]);
  }

  getLessonIndex(lessonId) {
    return this.modules
      .flatMap((module) => module.lessons)
      .findIndex((lesson) => lesson.id === lessonId);
  }

  getNextLesson() {
    const nextLesson = this.modules
      .flatMap((module) => module.lessons)
      .filter((lesson) => !lesson?.isCompleted())
      .first();

    if (!nextLesson) {
      const first = this.modules.first()?.lessons?.first();
      first.index = '01';
      return first;
    }

    const lessonIndex = this.getLessonIndex(nextLesson.id);
    nextLesson.index = +lessonIndex + 1;

    return nextLesson;
  }

  getNextLessonFrom(lessonId) {
    const lessons = this.modules.flatMap((module) => module.lessons);
    const lessonIndex = lessons.findIndex((lesson) => lesson.id === lessonId);

    return lessons
      .slice(+lessonIndex + 1)
      .concat(lessons.slice(0, lessonIndex))
      .filter((lesson) => !lesson?.isCompleted())
      .first();
  }

  getCompletedAtFormmated() {
    return formatISODate(this.completedAt, 'do LLLL yyyy');
  }

  getTotalLessonsStarted() {
    return this.modules.reduce((acc, module) => +acc + +module.getTotalLessonsStarted(), 0);
  }

  getLastLessonCompletedDate() {
    return this.modules
      .flatMap((module) => module.lessons)
      .filter((lesson) => lesson.isCompleted())
      .map((lesson) => lesson.completedAt)
      .sort((lessonADate, lessonBDate) => lessonADate - lessonBDate)
      .last();
  }
}
